import Popup from '@jo-v1/features/Popup';
import { useEffect, useRef } from 'react';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import {
  selectIsComparisonModalOpen,
  setIsComparisonModalOpen,
  selectProgramsToCompare,
} from '@core/reducers/matchesSlice';

import Card from './Card';
import styles from './JoComparisonModal.module.css';

const ComparisonModal = ({ squareButtons = false }) => {
  const dispatch = useDispatch();
  const showComparisonModal = useSelector(selectIsComparisonModalOpen);
  const programsToCompare = useSelector(selectProgramsToCompare);

  const { elementViewed, elementClicked } = useElementEvents({
    webElement: {
      elementType: 'compare-feature',
      location: 'compare-voyager',
      htmlId: 'modal-table-view',
      text: 'compare-programs',
      name: 'compare-voyager-modal',
    },
  });

  // Only show the popup if results are available
  useEffect(() => {
    if (showComparisonModal) elementViewed();
  }, [showComparisonModal]);

  // This onClose is fired when the user closes the modal via the `X` button or clicking outside of the modal
  const onClose = () => {
    elementClicked({
      webElement: {
        elementType: 'compare-feature',
        location: 'compare-voyager',
        htmlId: 'x-modal',
        name: 'x-modal',
        text: 'See Comparison',
      },
    });
    dispatch(setIsComparisonModalOpen(false));
  };

  const handleChooseMoreSchoolsClick = () => {
    elementClicked({
      webElement: {
        elementType: 'compare-feature',
        location: 'compare-voyager',
        htmlId: 'exit-modal-to-results',
        name: 'choose-more-schools',
        text: 'Choose more schools',
      },
    });
    dispatch(setIsComparisonModalOpen(false));
  };

  const cardTopRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const handleResize = () => {
      cardTopRefs.current.forEach((ref) => {
        if (ref) ref.style.height = 'auto';
      });

      const heights = cardTopRefs.current.map((ref) => ref?.offsetHeight || 0);
      const maxHeight = Math.max(...heights);

      cardTopRefs.current.forEach((ref) => {
        if (ref) ref.style.height = `${maxHeight}px`;
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [programsToCompare, cardTopRefs, showComparisonModal]);

  return (
    <Popup show={showComparisonModal} onClose={onClose}>
      <div className={styles.JoComparisonModal}>
        <h2 className={styles.title}>Compare Programs</h2>
        <p className={styles.description}>
          Compare up to 3 credit programs from our partners using the side-by-side card comparison tool below.
        </p>
        <div className={styles.cardsContainer}>
          {programsToCompare.map((result, index) => (
            <Card
              squareButtons={squareButtons}
              key={result?.program?.id}
              result={result}
              position={index + 1}
              refSetter={(el) => {
                cardTopRefs.current[index] = el;
              }}
            />
          ))}
        </div>
        <button className={styles.moreButton} type="button" onClick={handleChooseMoreSchoolsClick}>
          <BsArrowLeft size={24} />
          <span>Choose more schools</span>
        </button>
      </div>
    </Popup>
  );
};

export default ComparisonModal;
